/* eslint-disable react/no-array-index-key */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import NumberField from 'components/number-field';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { Controller, UseFieldArrayReturn, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { chargesPayeePayerForm } from '../services';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { AccountingType } from 'v2-types/order';

const useStyles = makeStyles((theme: any) => ({
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },

      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        padding: 8,
      },
    },
  },
  partyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  partyInfo: {
    flex: 1,
    minWidth: 0,
  },
  partyName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeButton: {
    padding: 0,
    minWidth: 44,
    minHeight: 44,
    flexShrink: 0,
  },
  amountFieldLabel: {
    fontWeight: 500,
    color: 'black',
    display: 'block',
    marginBottom: theme.spacing(0.5),
  },
  scrollContainer: {
    overflowY: 'scroll',
    padding: '0px 10px',
    [theme.breakpoints.up('md')]: {
      maxHeight: 400,
    },
  },
  mobileFieldBox: {
    flex: 1,
    minHeight: 44,
  },
}));

interface Props {
	methods: UseFormReturn<chargesPayeePayerForm>;
	partiesSelected: UseFieldArrayReturn<chargesPayeePayerForm, 'InvolvedParties', 'id'>;
	charges?: ChargeFormEntry[];
	accountingEntry?: AccountingType;
	hideAtClosingAmount?: boolean;
	hideBeforeClosingAmount?: boolean;
}

export const SelectedPartiesList: FC<Props> = ({
  methods,
  partiesSelected,
  charges,
  accountingEntry,
  hideAtClosingAmount,
  hideBeforeClosingAmount,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { setValue, getValues } = methods;
  const watchedType = useWatch({ control: methods.control, name: 'typeAmount' });

  const fullAmount = (charges && charges.length ? charges[0].amount : accountingEntry?.amount) ?? 0;
  const isSingleAccountingEntry = (charges && charges.length === 1) || !!accountingEntry;

  const calculatePercent = useCallback((value: number) => {
    if (value <= 0 || fullAmount <= 0) return 0;
    return (value / fullAmount) * 100;
  }, [fullAmount]);

  const calculateAmount = useCallback((value: number) => {
    let amount = 0;

    if (value > 0) {
      amount = fullAmount * (value / 100);
    }

    return amount;
  }, [fullAmount]);

  const prevTypeRef = useRef(watchedType);

  useEffect(() => {
    if (prevTypeRef.current === watchedType) return;
    prevTypeRef.current = watchedType;

    const parties = getValues('InvolvedParties');
    if (!parties || parties.length === 0) return;

    if (watchedType === 'Percent') {
      parties.forEach((party, index) => {
        setValue(
          `InvolvedParties.${index}.at_closing_percent`,
          calculatePercent(party.at_closing_amount || 0)
        );
        setValue(
          `InvolvedParties.${index}.before_closing_percent`,
          calculatePercent(party.before_closing_amount || 0)
        );
      });
    } else if (watchedType === 'Amount' && isSingleAccountingEntry) {
      parties.forEach((party, index) => {
        setValue(
          `InvolvedParties.${index}.at_closing_amount`,
          calculateAmount(party.at_closing_percent || 0)
        );
        setValue(
          `InvolvedParties.${index}.before_closing_amount`,
          calculateAmount(party.before_closing_percent || 0)
        );
      });
    }
  }, [watchedType, getValues, setValue, calculateAmount, calculatePercent, isSingleAccountingEntry]);

  return (
    <Grid
      container
      direction="column"
    >
      <Grid item xs={12}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={isMobile ? 12 : 6}>
            <Typography variant="body1" style={{ fontWeight: 500, color: theme.palette.primary.light }}>
              {t('charges:partiesSelected')}
            </Typography>
          </Grid>
          {!isMobile && (
          <Grid item xs={6}>
            <Grid container direction="row" justifyContent="space-around">
              {!hideBeforeClosingAmount && (
              <Grid item xs={5} style={{ justifyItems: 'center' }}>
                <Typography variant="body1" style={{ fontWeight: 500, color: 'black' }}>
                  {t('charges:beforeClosing')}
                </Typography>
              </Grid>
              )}
              {!hideAtClosingAmount && (
              <Grid item xs={5} style={{ justifyItems: 'center' }}>
                <Typography variant="body1" style={{ fontWeight: 500, color: 'black' }}>
                  {t('charges:atClosing')}
                </Typography>
              </Grid>
              )}
            </Grid>
          </Grid>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        container
        className={classes.scrollContainer}
        spacing={3}
      >
        {[...partiesSelected.fields.map((party, index) => ({ party, index }))]
				  .sort((a, b) => a.party.name.localeCompare(b.party.name))
				  .map(({ party, index }) => {
				    const beforeClosingField = watchedType === 'Amount' ? (
  <Controller
    control={methods.control}
    name={`InvolvedParties.${index}.before_closing_amount`}
    key={`InvolvedParties.${index}.before_closing_amount`}
    render={({ field }) => (
      <NumberField
        {...field}
        naked
        color="secondary"
        onChange={undefined}
        value={getValues(`InvolvedParties.${index}.before_closing_amount`)}
        prefix={watchedType === 'Amount' ? '$' : undefined}
        onValueChange={({ floatValue }) => {
										  field.onChange(floatValue);
        }}
        handleBlur={() =>
										  setValue(
										    `InvolvedParties.${index}.before_closing_percent`,
										    calculatePercent(field.value)
										  )}
        decimalScale={2}
        thousandSeparator
        className={classes.numberField}
        variant="outlined"
      />
    )}
  />
				    ) : (
  <Controller
    control={methods.control}
    name={`InvolvedParties.${index}.before_closing_percent`}
    key={`InvolvedParties.${index}.before_closing_percent`}
    render={({ field }) => (
      <NumberField
        {...field}
        naked
        value={getValues(`InvolvedParties.${index}.before_closing_percent`)}
        color="secondary"
        onChange={undefined}
        suffix="%"
        onValueChange={({ floatValue }) => {
										  field.onChange(floatValue ?? 0);
        }}
        handleBlur={() => {
										  if (isSingleAccountingEntry) {
										    setValue(
										      `InvolvedParties.${index}.before_closing_amount`,
										      calculateAmount(field.value)
										    );
										  }
        }}
        decimalScale={2}
        thousandSeparator
        className={classes.numberField}
        variant="outlined"
      />
    )}
  />
				    );

				    const atClosingField = watchedType === 'Amount' ? (
  <Controller
    control={methods.control}
    name={`InvolvedParties.${index}.at_closing_amount`}
    key={`InvolvedParties.${index}.at_closing_amount`}
    render={({ field }) => (
      <NumberField
        {...field}
        naked
        onChange={undefined}
        onValueChange={({ floatValue }) => {
										  field.onChange(floatValue);
        }}
        value={getValues(`InvolvedParties.${index}.at_closing_amount`)}
        thousandSeparator
        handleBlur={() =>
										  setValue(
										    `InvolvedParties.${index}.at_closing_percent`,
										    calculatePercent(field.value)
										  )}
        prefix="$"
        color="secondary"
        decimalScale={2}
        className={classes.numberField}
        variant="outlined"
      />
    )}
  />
				    ) : (
  <Controller
    control={methods.control}
    name={`InvolvedParties.${index}.at_closing_percent`}
    key={`InvolvedParties.${index}.at_closing_percent`}
    render={({ field }) => (
      <NumberField
        {...field}
        naked
        onChange={undefined}
        onValueChange={({ floatValue }) => {
										  field.onChange(floatValue ?? 0);
        }}
        value={getValues(`InvolvedParties.${index}.at_closing_percent`)}
        handleBlur={() => {
										  if (isSingleAccountingEntry) {
										    setValue(
										      `InvolvedParties.${index}.at_closing_amount`,
										      calculateAmount(field.value)
										    );
										  }
        }}
        thousandSeparator
        suffix="%"
        color="secondary"
        decimalScale={2}
        className={classes.numberField}
        variant="outlined"
      />
    )}
  />
				    );

				    return (
  <Grid item xs={12} key={index}>
    <Grid container direction="row" spacing={2} alignContent="center">
      <Grid item xs={isMobile ? 12 : 6} style={{ alignContent: 'center' }}>
        <Box className={classes.partyRow}>

          <Box className={classes.partyInfo}>
            <Typography variant="body2" className={classes.partyName} style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
              {party.name}
            </Typography>
            <Typography variant="caption" className={classes.partyName} style={{ display: 'block' }}>
              {party.partyKinds.join(", ")}
            </Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={isMobile ? 12 : 6} style={{ alignContent: 'center' }}>
        {isMobile ? (
          <Box style={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(1), justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {!hideBeforeClosingAmount && (
            <Box className={classes.mobileFieldBox}>
              <Typography variant="caption" className={classes.amountFieldLabel}>
                {t('charges:beforeClosing')}
              </Typography>
              {beforeClosingField}
            </Box>
            )}
            {!hideAtClosingAmount && (
            <Box className={classes.mobileFieldBox}>
              <Typography variant="caption" className={classes.amountFieldLabel}>
                {t('charges:atClosing')}
              </Typography>
              {atClosingField}
            </Box>
            )}
          </Box>
        ) : (
          <Grid container direction="row" justifyContent="space-around">
            {!hideBeforeClosingAmount && <Grid item xs={5}>{beforeClosingField}</Grid>}
            {!hideAtClosingAmount && <Grid item xs={5}>{atClosingField}</Grid>}
          </Grid>
        )}
      </Grid>
    </Grid>
  </Grid>
				    );
				  })}
      </Grid>
    </Grid>
  );
};
